<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  getAnalyticsConsent,
  isAnalyticsConfigured,
  onAnalyticsConsentChange,
  onOpenAnalyticsConsentPreferences,
  setAnalyticsConsent,
  trackPageview,
} from '@/utils/analytics'

const route = useRoute()
const visible = ref(false)
const saving = ref('')
let removeConsentListener = null
let removeOpenListener = null

function refreshVisibility() {
  visible.value = isAnalyticsConfigured() && getAnalyticsConsent() === null
}

async function choose(consent) {
  if (saving.value) return
  saving.value = consent
  try {
    await setAnalyticsConsent(consent)
    visible.value = false
    if (consent === 'granted') trackPageview(route.fullPath)
  } finally {
    saving.value = ''
  }
}

onMounted(() => {
  refreshVisibility()
  removeConsentListener = onAnalyticsConsentChange(refreshVisibility)
  removeOpenListener = onOpenAnalyticsConsentPreferences(() => {
    if (isAnalyticsConfigured()) visible.value = true
  })
})

onBeforeUnmount(() => {
  removeConsentListener?.()
  removeOpenListener?.()
})
</script>

<template>
  <section v-if="visible" class="consent" aria-label="Analytics preferences">
    <div class="consent-copy">
      <p class="consent-kicker">Analytics</p>
      <h2>Help the coach learn what works.</h2>
      <p>
        We use PostHog for product analytics and masked session recordings. No ads,
        no resale, no raw chess password nonsense. You can say no.
      </p>
    </div>
    <div class="consent-actions">
      <button
        class="consent-btn consent-btn--quiet"
        type="button"
        :disabled="Boolean(saving)"
        @click="choose('denied')"
      >
        No thanks
      </button>
      <button
        class="consent-btn consent-btn--primary"
        type="button"
        :disabled="Boolean(saving)"
        @click="choose('granted')"
      >
        Allow analytics
      </button>
    </div>
  </section>
</template>

<style scoped>
.consent {
  position: fixed;
  right: var(--space-md);
  bottom: calc(var(--space-md) + env(safe-area-inset-bottom, 0px));
  left: var(--space-md);
  z-index: 80;
  display: grid;
  gap: var(--space-md);
  max-width: 720px;
  border: 1px solid var(--hairline);
  border-radius: var(--radius-md);
  background: rgba(var(--paper-rgb), 0.98);
  box-shadow: 0 18px 48px rgba(var(--ink-rgb), 0.16);
  padding: var(--space-md);
}
.consent-kicker {
  color: var(--ink-gold);
  font-size: var(--text-xs);
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}
.consent h2 {
  margin-top: var(--space-2xs);
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: 800;
  line-height: 1.05;
}
.consent p:last-child {
  margin-top: var(--space-xs);
  color: var(--muted);
  font-size: var(--text-sm);
  line-height: 1.45;
}
.consent-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}
.consent-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border-radius: var(--radius-md);
  font: inherit;
  font-weight: 800;
  padding: 0 var(--space-md);
  cursor: pointer;
  transition:
    background-color var(--motion-fast) var(--ease-out),
    border-color var(--motion-fast) var(--ease-out),
    box-shadow var(--motion-fast) var(--ease-out),
    transform var(--motion-fast) var(--ease-press);
}
.consent-btn--quiet {
  border: 1px solid var(--hairline);
  background: var(--paper);
  color: var(--ink);
}
.consent-btn--primary {
  border: 0;
  background: var(--someday-gold);
  color: var(--ink);
}
.consent-btn:not(:disabled):hover {
  transform: translateY(-1px);
}
.consent-btn--quiet:not(:disabled):hover {
  border-color: var(--someday-gold);
  background: var(--surface);
}
.consent-btn--primary:not(:disabled):hover {
  background: var(--someday-gold-hover);
  box-shadow: var(--shadow-action);
}
.consent-btn:not(:disabled):active {
  box-shadow: none;
  transform: translateY(1px) scale(0.99);
}
.consent-btn:disabled {
  opacity: 0.65;
  cursor: progress;
}
@media (min-width: 768px) {
  .consent {
    right: var(--space-lg);
    bottom: var(--space-lg);
    left: auto;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    padding: var(--space-lg);
  }
  .consent-actions {
    justify-content: flex-end;
  }
}
@media (max-width: 767px) {
  .consent {
    bottom: calc(76px + env(safe-area-inset-bottom, 0px));
  }
  .consent-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}
</style>
